import type * as THREE from 'three'
import type { FragmentsModel } from '@thatopen/fragments'
import { viewerApi } from '@viewer/lib/viewerApi'
import { fragmentsReturnsWorld } from '@viewer/lib/geoTransform'
import type { ClashModelSource } from '@viewer/clash/lib'
import type { ClashModelInventory } from './clashSets'

// ---------------------------------------------------------------------------
// Ponte entre os modelos carregados no viewer e o motor da Fase 1.
//
// Duas coisas acontecem aqui.
//
// 1. INVENTARIO. Que categorias IFC tem cada modelo e com quantos
//    elementos COM GEOMETRIA. E o que enche os selectores dos conjuntos
//    A x B com contagens reais ("IFCBEAM (312)") em vez de uma lista
//    generica onde metade nao existe no ficheiro. Filtra-se pelos ids
//    com geometria porque so esses podem colidir: contar IfcPropertySet
//    era mentir ao utilizador sobre o tamanho da analise.
//
// 2. CAIXAS EM MUNDO. O `runClash` pede `getBoxes` e assume MUNDO,
//    enquanto a geometria passa pelo `collectWorldTriangles`, que aplica
//    a `matrixWorld` do modelo. Na versao actual do Fragments o
//    `getBoxes` ja devolve mundo, mas nem sempre foi assim e o viewer
//    tem um detector para isso (`fragmentsReturnsWorld`, usado pelo
//    Focar/orbit). Num modelo com placement manual ou alinhamento de
//    federacao, as duas fontes divergiriam e a broad-phase apontaria
//    para o sitio errado. O `clashModelSource` envolve o `getBoxes` com
//    a mesma correccao, para as caixas e os triangulos falarem sempre do
//    mesmo sitio.
// ---------------------------------------------------------------------------

interface InventoryCacheEntry {
  key: string
  inventory: ClashModelInventory
}

/** O mapa categoria -> ids e imutavel enquanto o modelo estiver
 *  carregado, e varre-lo custa uma ida ao worker do Fragments. */
const inventoryCache = new Map<string, InventoryCacheEntry>()

export function forgetClashInventory(modelId?: string): void {
  if (modelId) inventoryCache.delete(modelId)
  else inventoryCache.clear()
}

async function buildInventory(
  modelId: string,
  fileName: string,
): Promise<ClashModelInventory> {
  const empty: ClashModelInventory = {
    modelId,
    fileName,
    byCategory: new Map(),
    total: 0,
  }
  const model = viewerApi.getModel(modelId)
  if (!model) return empty

  let geometric = new Set<number>()
  try {
    geometric = new Set(await model.getItemsIdsWithGeometry())
  } catch (err) {
    console.warn('[clash] getItemsIdsWithGeometry falhou', err)
  }
  let raw: Record<string, number[]> = {}
  try {
    raw = await model.getItemsOfCategories([/.*/])
  } catch (err) {
    console.warn('[clash] getItemsOfCategories falhou', err)
  }

  const byCategory = new Map<string, number[]>()
  let total = 0
  for (const [cat, ids] of Object.entries(raw)) {
    if (!ids || ids.length === 0) continue
    const onlyGeo =
      geometric.size > 0 ? ids.filter((id) => geometric.has(id)) : ids
    if (onlyGeo.length === 0) continue
    const upper = cat.toUpperCase()
    const existing = byCategory.get(upper)
    if (existing) existing.push(...onlyGeo)
    else byCategory.set(upper, [...onlyGeo])
    total += onlyGeo.length
  }
  return { modelId, fileName, byCategory, total }
}

/** Inventario de todos os modelos carregados. Serve o painel; e
 *  reaproveitado enquanto o modelo nao mudar de nome nem de id. */
export async function loadInventories(
  models: readonly { id: string; fileName: string }[],
): Promise<ClashModelInventory[]> {
  const out: ClashModelInventory[] = []
  for (const m of models) {
    const key = `${m.id} ${m.fileName}`
    const cached = inventoryCache.get(m.id)
    if (cached && cached.key === key) {
      out.push(cached.inventory)
      continue
    }
    const inventory = await buildInventory(m.id, m.fileName)
    inventoryCache.set(m.id, { key, inventory })
    out.push(inventory)
  }
  // Modelos descarregados nao ficam a segurar mapas de ids.
  const live = new Set(models.map((m) => m.id))
  for (const id of [...inventoryCache.keys()]) {
    if (!live.has(id)) inventoryCache.delete(id)
  }
  return out
}

interface BoxLike {
  min: { x: number; y: number; z: number }
  max: { x: number; y: number; z: number }
}

/** Envolve um FragmentsModel no que o motor precisa, com as caixas
 *  garantidamente em coordenadas de MUNDO. */
export function clashModelSource(model: FragmentsModel): ClashModelSource {
  return {
    modelId: model.modelId,
    object: model.object,
    getItemsIdsWithGeometry: () => model.getItemsIdsWithGeometry(),
    getItemsGeometry: (ids: number[]) => model.getItemsGeometry(ids),
    getItemsOfCategories: (categories: RegExp[]) =>
      model.getItemsOfCategories(categories),
    getBoxes: async (ids: number[]): Promise<BoxLike[]> => {
      const boxes = (await model
        .getBoxes(ids)
        .catch(() => [] as THREE.Box3[])) as THREE.Box3[]
      if (fragmentsReturnsWorld(model)) return boxes
      model.object.updateMatrixWorld(true)
      const mw = model.object.matrixWorld
      return boxes.map((b) =>
        b && !b.isEmpty() ? b.clone().applyMatrix4(mw) : b,
      )
    },
  }
}

/** Fontes para os modelos indicados, pela ordem dada. Modelos que ja
 *  nao estejam registados sao ignorados em silencio. */
export function clashModelSources(
  modelIds: readonly string[],
): ClashModelSource[] {
  const out: ClashModelSource[] = []
  for (const id of modelIds) {
    const model = viewerApi.getModel(id)
    if (!model) continue
    out.push(clashModelSource(model))
  }
  return out
}

/** `categoryOf` para o `runClash`: evita que ele volte a varrer o modelo
 *  categoria a categoria quando o painel ja tem o inventario em mao. */
export function categoryLookup(
  inventories: readonly ClashModelInventory[],
): (modelId: string, localId: number) => string | null {
  const byModel = new Map<string, Map<number, string>>()
  for (const inv of inventories) {
    const map = new Map<number, string>()
    for (const [cat, ids] of inv.byCategory) {
      for (const id of ids) map.set(id, cat)
    }
    byModel.set(inv.modelId, map)
  }
  return (modelId, localId) => byModel.get(modelId)?.get(localId) ?? null
}

/** GlobalId dos elementos de um modelo, na ordem pedida. Best-effort:
 *  um modelo sem a API devolve nulos e a UI segue sem GUIDs. */
export async function guidsFor(
  modelId: string,
  localIds: number[],
): Promise<(string | null)[]> {
  const model = viewerApi.getModel(modelId)
  if (!model || localIds.length === 0) return localIds.map(() => null)
  try {
    const guids = await model.getGuidsByLocalIds(localIds)
    return localIds.map((_, i) =>
      typeof guids[i] === 'string' && guids[i] ? (guids[i] as string) : null,
    )
  } catch {
    return localIds.map(() => null)
  }
}

/** Nome legivel de um elemento, para a lista de conflitos. Best-effort:
 *  sem nome devolve null e a UI mostra so a categoria e o id. */
export async function namesFor(
  modelId: string,
  localIds: number[],
): Promise<(string | null)[]> {
  const model = viewerApi.getModel(modelId)
  if (!model || localIds.length === 0) return localIds.map(() => null)
  try {
    const data = await model.getItemsData(localIds, {
      attributesDefault: false,
      attributes: ['Name'],
    })
    return localIds.map((_, i) => {
      const entry = data[i] as { Name?: { value?: unknown } } | undefined
      const value = entry?.Name?.value
      return typeof value === 'string' && value ? value : null
    })
  } catch {
    return localIds.map(() => null)
  }
}
